/* eslint-disable */

/*----------------------------------------------------------------------------------------------*
 * You MUST NOT modify this file manually because it is automatically generated by gen-i18n-ts. *
 * Modify your JSON files in i18n directory and run gen-i18n-ts script to regenerate this file. *
 *----------------------------------------------------------------------------------------------*/

'use client';

import { useParams, usePathname, useRouter } from 'next/navigation';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useInsertionEffect, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  i18n,
  isSupportedLanguage,
  matchSupportedLanguage,
} from "./i18n";
import type { SupportedLanguage } from "./i18n";

interface LocaleContextValue {
  locale: SupportedLanguage;
  setLocale: (locale: string) => boolean;
}

interface LocaleProviderProps {
  children: React.ReactNode;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export const LOCALE_COOKIE_NAME = 'preferred-locale';
const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const params = useParams<{ locale?: string | string[] }>();
  const pathname = usePathname();
  const router = useRouter();

  const routeLocale = Array.isArray(params.locale) ? params.locale[0] : params.locale;
  const [detectedLocale, setDetectedLocale] = useState<SupportedLanguage | undefined>();
  const locale = isSupportedLanguage(routeLocale) ? routeLocale : detectedLocale ?? DEFAULT_LANGUAGE;
  useEffect(() => {
    if (isSupportedLanguage(routeLocale)) return;
    setDetectedLocale(detectLocaleFromBrowser());
  }, [routeLocale]);

  // setLocale reads the pathname through a ref so that it, and the context value, stay stable across navigations;
  // otherwise every useI18n consumer would re-render whenever the path changes. The ref is updated in an insertion
  // effect because it runs before every layout and passive effect of the commit, including those of descendants that
  // may call setLocale, whereas a passive effect here would run after them and leave the previous pathname.
  const pathnameRef = useRef(pathname);
  useInsertionEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const setLocale = useCallback(
    (newLocale: string): boolean => {
      if (!isSupportedLanguage(newLocale)) {
        console.warn(`Locale "${newLocale}" is not supported. Supported locales:`, SUPPORTED_LANGUAGES);
        return false;
      }

      setLocalePreference(newLocale);
      const nextPathname = replaceLocaleSegment(pathnameRef.current, newLocale);
      const query = getCurrentSearchParams();
      const hash = getCurrentHash();
      router.push(`${nextPathname}${query ? `?${query}` : ''}${hash}`);
      return true;
    },
    [router]
  );
  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export function useI18n(): ReturnType<typeof i18n> {
  return i18n(useLocaleValue());
}

export function useLocaleValue(): SupportedLanguage {
  return useLocaleContext().locale;
}

export function useSetLocale(): (locale: string) => boolean {
  return useLocaleContext().setLocale;
}

function useLocaleContext(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('LocaleProvider is missing.');
  return context;
}

function detectLocaleFromBrowser(): SupportedLanguage {
  if (!('window' in globalThis)) return DEFAULT_LANGUAGE;

  try {
    const storedLocale =
      localStorage.getItem(LOCALE_COOKIE_NAME) ||
      document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${LOCALE_COOKIE_NAME}=`))
        ?.split('=')[1];
    if (isSupportedLanguage(storedLocale)) return storedLocale;
  } catch {
    // Storage may be unavailable even when navigator.language is still usable.
  }

  const browserLanguages = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const browserLanguage of browserLanguages) {
    const matchedLocale = matchSupportedLanguage(browserLanguage);
    if (matchedLocale) return matchedLocale;
  }
  return DEFAULT_LANGUAGE;
}

function setLocalePreference(locale: SupportedLanguage): void {
  if (!('window' in globalThis)) return;

  try {
    localStorage.setItem(LOCALE_COOKIE_NAME, locale);
    document.cookie = `${LOCALE_COOKIE_NAME}=${locale}; max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
  } catch {
    return;
  }
}

function getCurrentSearchParams(): string {
  if (!('window' in globalThis)) return '';
  return globalThis.location.search.replace(/^\?/, '');
}

function getCurrentHash(): string {
  if (!('window' in globalThis)) return '';
  return globalThis.location.hash;
}

function replaceLocaleSegment(pathname: string, nextLocale: SupportedLanguage): string {
  const segments = pathname.split('/');
  if (isSupportedLanguage(segments[1])) {
    segments[1] = nextLocale;
    return segments.join('/');
  }
  return `/${nextLocale}${pathname}`;
}
